import { UserManagerSettings, WebStorageStateStore } from 'oidc-client-ts';
import { getDynamicRedirectUri, getDynamicPostLogoutRedirectUri } from '../utils/authUtils';
import { isNativeApp } from '../utils/nativePlatform';

/**
 * Feature flag to enable/disable OIDC authentication option.
 *
 * On the native (iOS) app OIDC is the only supported provider, so it is always
 * enabled regardless of the build-time env flag. On web it stays gated behind
 * REACT_APP_OIDC_ENABLED so existing behavior is unchanged.
 */
export const OIDC_ENABLED =
  process.env.REACT_APP_OIDC_ENABLED === 'true' || isNativeApp();

/**
 * OIDC Provider Configuration for renewmyday.com
 * 
 * This is an alternative authentication provider to Azure AD (Entra ID).
 * Both providers are supported and users can choose which one to use.
 * 
 * NOTE: We manually specify all endpoints to avoid CORS issues with the
 * discovery endpoint (.well-known/openid-configuration)
 */
export const oidcConfig: UserManagerSettings = {
  // Provider authority (issuer)
  authority: 'https://renewmyday.com',
  
  // Manually specify metadata to avoid CORS issues with discovery endpoint
  // These values are from https://renewmyday.com/.well-known/openid-configuration
  metadata: {
    issuer: 'https://renewmyday.com',
    authorization_endpoint: 'https://renewmyday.com/oauth/authorize',
    token_endpoint: 'https://renewmyday.com/oauth/token',
    jwks_uri: 'https://renewmyday.com/oauth/jwks',
    scopes_supported: ['openid', 'profile', 'email', 'phone', 'address'],
    response_types_supported: ['code', 'token'],
    subject_types_supported: ['public'],
    token_endpoint_auth_methods_supported: ['client_secret_basic', 'client_secret_post'],
  },
  
  // Client configuration (SPA - no secret required)
  client_id: process.env.REACT_APP_OIDC_CLIENT_ID || '6',
  
  // Redirect URIs - use same dynamic URIs as MSAL
  redirect_uri: getDynamicRedirectUri(),
  post_logout_redirect_uri: getDynamicPostLogoutRedirectUri(),
  
  // Response type for authorization code flow with PKCE
  // Note: Token exchange requires backend proxy due to CORS issues with provider
  response_type: 'code',
  
  // Scopes - same as MSAL login request.
  // The provider does not support the `offline_access` scope (not in its
  // scopes_supported list). Its authorization-code grant returns a refresh_token
  // without it, which we persist on native for login that survives app restarts.
  scope: 'openid profile email',
  
  // Storage configuration.
  // On native (iOS) sessionStorage is cleared when the app is terminated, which
  // logs the user out on every restart. Use localStorage on native so the signed-in
  // user (and refresh token) survives app restarts. Web keeps sessionStorage so its
  // existing behavior is unchanged. The stateStore stays on sessionStorage because it
  // only holds transient in-flight PKCE state during a single sign-in round trip.
  userStore: new WebStorageStateStore({
    store: isNativeApp() ? window.localStorage : window.sessionStorage,
  }),
  stateStore: new WebStorageStateStore({ store: window.sessionStorage }),
  
  // Automatic token management
  automaticSilentRenew: false, // Disable since we're not using silent renew iframe
  
  // Load user info from userinfo endpoint
  loadUserInfo: false, // Disable if userinfo endpoint has CORS issues too
  
  // Monitor session state - disable to avoid CORS issues with check_session_iframe
  monitorSession: false,
  
  // Filter protocol claims from user profile
  filterProtocolClaims: true,
};

/**
 * OIDC login request configuration
 * Mirrors the MSAL loginRequest for consistency
 */
export const oidcLoginRequest = {
  scope: 'openid profile email',
};

/**
 * Check if OIDC authentication is available
 */
export const isOidcAvailable = (): boolean => {
  // The native app uses a default client id ('6') and must always offer OIDC.
  return OIDC_ENABLED && (!!process.env.REACT_APP_OIDC_CLIENT_ID || isNativeApp());
};
